import { ReportStatus, ResearchReports } from '@features/researches/models/report.model';
import { Sensor, SensorDetails } from '@features/researches/models/sensor.model';
import { Timezone } from '@features/researches/models/timezone.model';

export interface Research {
  details: ResearchDetails;
  workspace: WorkspaceDetails;
  structure: OrganisationalStructure;
  utilization: SpaceUtilization;
  companyLogoFileName: string;
  spacePlanFileName?: string;
}

export interface ResearchWithId {
  details: ResearchDetailsWithId;
  workspace: WorkspaceDetails;
  structure: OrganisationalStructure;
  utilization: SpaceUtilization;
  companyLogoFileName: string;
  spacePlanFileName?: string;
}

export interface ResearchWithFiles {
  research: ResearchWithId;
  companyLogoFile: File | null;
  spacePlanFileName: string;
}

export interface ResearchDetails {
  name: string;
  endDate: string;
  startDate: string;
  timezone: Timezone;
  funder: string;
  excludedDayRanges: ExcludedDayRange[];
  client: Client;
  draft: boolean;
  status?: ResearchStatus;
}

export interface ResearchDetailsWithId extends ResearchDetails {
  id: number;
}

export interface WorkspaceDetails {
  buildingName: string;
  avgSpacePerDesk: number;
  avgSpaceCost: number;
  availableFloors: AvailableFloor[];
  officeBenchmark: number;
  openSpaceBenchmark: number;
  workdays: string[];
  officeHours: OfficeHours;
  minDailyDeskCapacity: number;
  avgDailyDeskCapacity: number;
}

export interface OfficeHours {
  start: string;
  end: string;
}

export interface OrganisationalStructure {
  departments: Department[];
}

export interface SpaceUtilization {
  byTeams: TeamLocation[];
  additionalRooms: AdditionalRoom[];
}

export interface Client {
  id?: number;
  name: string;
  company: Company;
  numberOfEmployees: number;
  officeSpaceSize: number;
  industry: Industry;
}

export enum CompanySize {
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  BIG = 'BIG',
  LARGE = 'LARGE'
}

export interface ExcludedDayRange {
  id?: number;
  start: string;
  end: string;
  description: string;
}

export interface Company {
  id?: number;
  name: string;
  size: CompanySize;
}

export interface Industry {
  id: number;
  name: string;
}

export interface SearchableResearch {
  id: number;
  status: ResearchStatus;
  name: string;
  start: string;
  end: string;
  companyName: string;
  buildingName: string;
  created: string;
  modified: string;
  finalReportStatus: ReportStatus;
}

export enum ResearchStatus {
  ACTIVE = 'ACTIVE',
  FINISHED = 'FINISHED',
  READY = 'READY',
  IN_CONFIGURATION = 'IN_CONFIGURATION',
  DRAFT = 'DRAFT',
  ARCHIVED = 'ARCHIVED'
}

export interface AvailableFloor {
  id?: number;
  externalId: string;
  name: string;
}

export interface Team {
  name: string;
  numberOfEmployees: number;
  externalId: string;
}

export interface Department {
  name: string;
  externalId: string;
  teams: Team[];
}

export interface Location {
  externalId: string;
  spaceType: SpaceType;
  floorExternalId: string;
  totalSeats: number;
  area: number;
  sensorData?: Record<string, Sensor>;
}

export interface TeamLocation {
  teamExternalId: string;
  departmentExternalId: string;
  locations: Location[];
}

export interface AdditionalRoom {
  externalId: string;
  name: string;
  spaceType: SpaceType;
  capacity: number;
  area: number;
  floorExternalId: string;
  sensor?: Sensor;
}

export interface TechnicianNotifications {
  externalIds: string[];
}

export interface UserEmail {
  externalId: number;
  email: string;
  name: string;
}

export interface Participants {
  participantsIds: number[];
}

export enum SpaceType {
  OPEN_SPACE = 'OPEN_SPACE',
  DIRECTOR_OFFICE = 'DIRECTOR_OFFICE',
  BOARD_OFFICE = 'BOARD_OFFICE'
}

export enum AdditionalRoomType {
  CONFERENCE_ROOM = 'CONFERENCE_ROOM',
  SOCIAL = 'SOCIAL',
  NETWORK = 'NETWORK',
  TECH = 'TECH'
}

export interface ResearchPreview extends ResearchWithId {
  technicianNotifications?: string[];
  userNotifications?: UserEmail[];
  spacePlanFileUrl?: string;
  spacePlanFileName?: string;
  reports?: ResearchReports;
}

export interface ResearchSensorsSetup {
  research: ResearchWithId;
  sensorsData: SensorDetails[];
  spacePlanFileUrl?: string;
}

export interface ResearchShortInfo {
  id: number;
  name: string;
}

export interface PreviewSpaceUtilization {
  total: number;
  utilized: number;
  additional: number;
  desks: number;
  byDepartment: { [key: string]: { area: number; desks: number } };
}
